// Light/dark theme: a class on <html>, persisted per browser. Light is the default.
//
// A class and not a data attribute, because apps/docs runs next-themes and Fumadocs
// styles key off `.dark`. One selector across the monorepo beats two that have to agree.

export type Theme = "light" | "dark";

/** Present on <html> for dark, absent for light - the shape next-themes writes. */
export const THEME_CLASS = "dark";
export const THEME_STORAGE_KEY = "soma-theme";

export function isTheme(v: unknown): v is Theme {
  return v === "light" || v === "dark";
}

export function readTheme(): Theme {
  return document.documentElement.classList.contains(THEME_CLASS) ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle(THEME_CLASS, theme === "dark");
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage can be blocked (private mode); the theme still applies for this visit.
  }
}

/** Calls `onChange` whenever the theme class changes. Returns the unsubscribe function. */
export function subscribeTheme(onChange: () => void): () => void {
  const obs = new MutationObserver(onChange);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => obs.disconnect();
}

/**
 * Inlined in <head> so the stored theme is set before first paint - otherwise dark-mode
 * visitors see a white flash. Kept as a plain string: it runs before React exists.
 */
export const THEME_BOOT_SCRIPT = `(function(){try{if(localStorage.getItem("${THEME_STORAGE_KEY}")==="dark")document.documentElement.classList.add("${THEME_CLASS}");}catch(e){}})();`;

/** Resolved value of a CSS color token, e.g. readToken("--color-accent"). */
export function readToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}
